<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isOpen" class="wallet-selector-overlay" @click.self="close">
        <div class="wallet-selector-modal">
          <!-- 標題欄 -->
          <div class="wallet-selector-header">
            <h2>{{ $t('wallet.connect') }}</h2>
            <button class="close-button" @click="close" aria-label="關閉">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <!-- 選項卡 -->
          <div class="wallet-selector-tabs">
            <button
              :class="['tab-button', { active: activeTab === 'desktop' }]"
              @click="activeTab = 'desktop'"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
              {{ $t('wallet.desktop') }}
            </button>
            <button
              :class="['tab-button', { active: activeTab === 'mobile' }]"
              @click="activeTab = 'mobile'"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                <line x1="12" y1="18" x2="12.01" y2="18"></line>
              </svg>
              {{ $t('wallet.mobile') }}
            </button>
          </div>

          <!-- 搜索框 (僅在 Desktop 標籤顯示) -->
          <div v-if="activeTab === 'desktop'" class="wallet-search-container">
            <div class="wallet-search-input-wrapper">
              <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                class="wallet-search-input"
                :placeholder="$t('wallet.searchPlaceholder') || 'Search Wallet'"
                @input="filterWallets"
              />
              <div v-if="searchQuery" class="wallet-search-count">
                {{ filteredWallets.length }}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            </div>
          </div>

          <!-- Desktop 錢包選項 -->
          <div v-if="activeTab === 'desktop'" class="wallet-options">
            <!-- MetaMask -->
            <button
              v-if="shouldShowWallet('MetaMask')"
              class="wallet-option"
              :class="{ 'available': isMetaMaskAvailable, 'unavailable': !isMetaMaskAvailable }"
              @click="connectMetaMask"
              :disabled="!isMetaMaskAvailable || isConnecting || walletStore.isConnecting"
            >
              <div class="wallet-icon">
                <img src="https://raw.githubusercontent.com/MetaMask/brand-resources/master/SVG/metamask-fox.svg" alt="MetaMask" />
              </div>
              <div class="wallet-info">
                <div class="wallet-name">MetaMask</div>
                <div class="wallet-status">
                  <span v-if="isMetaMaskAvailable" class="status-available">✅ {{ $t('wallet.available') }}</span>
                  <span v-else class="status-unavailable">❌ {{ $t('wallet.notInstalled') }}</span>
                </div>
              </div>
              <div class="wallet-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            </button>

            <!-- Trust Wallet -->
            <button
              v-if="shouldShowWallet('Trust Wallet')"
              class="wallet-option"
              :class="{ 'available': isTrustWalletAvailable, 'unavailable': !isTrustWalletAvailable }"
              @click="connectTrustWallet"
              :disabled="!isTrustWalletAvailable || isConnecting || walletStore.isConnecting"
            >
              <div class="wallet-icon">
                <div class="trust-wallet-icon">TW</div>
              </div>
              <div class="wallet-info">
                <div class="wallet-name">Trust Wallet</div>
                <div class="wallet-status">
                  <span v-if="isTrustWalletAvailable" class="status-available">✅ {{ $t('wallet.available') }}</span>
                  <span v-else class="status-unavailable">❌ {{ $t('wallet.notInstalled') }}</span>
                </div>
              </div>
              <div class="wallet-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            </button>

            <!-- Coinbase Wallet -->
            <button
              v-if="shouldShowWallet('Coinbase Wallet')"
              class="wallet-option"
              :class="{ 'available': isCoinbaseAvailable, 'unavailable': !isCoinbaseAvailable }"
              @click="connectCoinbase"
              :disabled="!isCoinbaseAvailable || isConnecting || walletStore.isConnecting"
            >
              <div class="wallet-icon">
                <div class="coinbase-wallet-icon">CB</div>
              </div>
              <div class="wallet-info">
                <div class="wallet-name">Coinbase Wallet</div>
                <div class="wallet-status">
                  <span v-if="isCoinbaseAvailable" class="status-available">✅ {{ $t('wallet.available') }}</span>
                  <span v-else class="status-unavailable">❌ {{ $t('wallet.notInstalled') }}</span>
                </div>
              </div>
              <div class="wallet-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            </button>

            <!-- WalletConnect (通用) -->
            <button
              v-if="shouldShowWallet('WalletConnect')"
              class="wallet-option walletconnect-option"
              @click="connectWalletConnect"
              :disabled="isConnecting || walletStore.isConnecting"
            >
              <div class="wallet-icon">
                <div class="walletconnect-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
              </div>
              <div class="wallet-info">
                <div class="wallet-name">WalletConnect</div>
                <div class="wallet-status">
                  <span class="status-available">🔗 {{ $t('wallet.scanQR') }}</span>
                </div>
              </div>
              <div class="wallet-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            </button>
          </div>

          <!-- Mobile QR Code -->
          <div v-if="activeTab === 'mobile'" class="wallet-qr-section">
            <div class="qr-code-container">
              <div v-if="qrCodeUri" class="qr-code">
                <img v-if="qrCodeImage" :src="qrCodeImage" alt="WalletConnect QR Code" />
                <div v-else class="qr-loading">正在生成 QR 碼...</div>
              </div>
              <div v-else class="qr-code-placeholder">
                <div class="loading-spinner"></div>
                <p>{{ $t('wallet.generatingQR') }}</p>
              </div>
            </div>
            <p class="qr-instruction">{{ $t('wallet.scanQRInstruction') }}</p>
            <button v-if="qrCodeUri" class="copy-link-button" @click="copyLink">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
              {{ $t('wallet.copyLink') }}
            </button>
          </div>

          <!-- 錯誤訊息 -->
          <div v-if="error" class="wallet-error">
            {{ error }}
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useWalletStore } from '../stores/wallet'
import { useI18n } from 'vue-i18n'
import QRCode from 'qrcode'
import { logger } from '../utils/logger'

interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const walletStore = useWalletStore()

const activeTab = ref<'desktop' | 'mobile'>('desktop')
const isConnecting = ref(false)
const error = ref<string | null>(null)
const qrCodeUri = ref<string | null>(null)
const qrCodeImage = ref<string>('')

// 檢測錢包可用性
const isMetaMaskAvailable = ref(false)
const isTrustWalletAvailable = ref(false)
const isCoinbaseAvailable = ref(false)

// 搜索相關
const searchQuery = ref('')

// 判斷是否應該顯示某個錢包（用於搜索過濾）
function shouldShowWallet(walletName: string): boolean {
  // 隱藏 Trust Wallet 和 Coinbase Wallet
  if (walletName === 'Trust Wallet' || walletName === 'Coinbase Wallet') {
    return false
  }
  if (!searchQuery.value) {
    return true
  }
  const query = searchQuery.value.toLowerCase()
  return walletName.toLowerCase().includes(query)
}

// 過濾錢包（用於搜索）
function filterWallets() {
  // 這個函數主要用於觸發 computed 屬性 `filteredWallets`
  // 過濾邏輯在 computed 屬性內部
}

// 錢包列表（用於搜索功能）
const allWallets = computed(() => [
  { id: 'metamask', name: 'MetaMask', available: isMetaMaskAvailable.value },
  { id: 'walletconnect', name: 'WalletConnect', available: true }
])

const filteredWallets = computed(() => {
  if (!searchQuery.value) {
    return allWallets.value
  }
  const query = searchQuery.value.toLowerCase()
  return allWallets.value.filter(wallet =>
    wallet.name.toLowerCase().includes(query)
  )
})

// 檢測錢包
function detectWallets() {
  // 檢測 MetaMask
  if (typeof window !== 'undefined' && window.ethereum) {
    // 處理多 provider 情況
    const providers = (window.ethereum as any).providers
    const ethereum = providers && Array.isArray(providers)
      ? providers.find((p: any) => p.isMetaMask) || window.ethereum
      : window.ethereum
    
    isMetaMaskAvailable.value = !!(ethereum?.isMetaMask)
    
    // 檢測 Trust Wallet
    const trustWallet = providers && Array.isArray(providers)
      ? providers.find((p: any) => p.isTrust) || window.ethereum
      : window.ethereum
    isTrustWalletAvailable.value = !!(trustWallet?.isTrust)
    
    // 檢測 Coinbase Wallet
    const coinbase = providers && Array.isArray(providers)
      ? providers.find((p: any) => p.isCoinbaseWallet) || window.ethereum
      : window.ethereum
    isCoinbaseAvailable.value = !!(coinbase?.isCoinbaseWallet)
  }
}

// 獲取 MetaMask provider
function getMetaMaskProvider() {
  if (typeof window === 'undefined' || !window.ethereum) return null
  
  const providers = (window.ethereum as any).providers
  if (providers && Array.isArray(providers)) {
    return providers.find((p: any) => p.isMetaMask) || window.ethereum
  }
  return window.ethereum.isMetaMask ? window.ethereum : null
}

// 連接 MetaMask
async function connectMetaMask() {
  try {
    isConnecting.value = true
    error.value = null

    // 優先使用瀏覽器擴展（如果已安裝）
    const metaMaskProvider = getMetaMaskProvider()
    if (metaMaskProvider) {
      try {
        // 直接使用 MetaMask 擴展連接（不需要 WalletConnect）
        const accounts = await metaMaskProvider.request({ 
          method: 'eth_requestAccounts' 
        })
        
        if (accounts && accounts.length > 0) {
          // 獲取鏈 ID
          const chainIdHex = await metaMaskProvider.request({ 
            method: 'eth_chainId' 
          })
          const chainId = parseInt(chainIdHex, 16)
          
          // 更新錢包狀態
          walletStore.setDirectConnection({
            address: accounts[0],
            chainId
          })
          
          // 檢查並切換到 BSC
          try {
            await walletStore.ensureBSCConnection()
          } catch (err) {
            logger.warn('切換到 BSC 失敗，但連接已成功')
          }
          
          emit('close')
          return
        }
      } catch (err: any) {
        // 如果用戶拒絕連接，拋出錯誤
        if (err.code === 4001) {
          throw new Error('用戶拒絕連接請求')
        }
        throw err
      }
    }

    // 如果沒有安裝 MetaMask 擴展，使用 WalletConnect
    // 確保連接器已初始化
    if (!walletStore.connector) {
      await walletStore.initConnector()
    }

    if (!walletStore.connector) {
      throw new Error('連接器初始化失敗')
    }

    // 通過 WalletConnect 連接 MetaMask（用於手機錢包）
    try {
      await walletStore.connectWalletById('c57ca95b47569778a828d19178114f4a188251b5')
      emit('close')
      return
    } catch (err) {
      // WalletConnect 連接失敗
      // 如果 WalletConnect 也失敗，使用通用連接
      const { session: providerSession } = await walletStore.connector.connect()
      if (providerSession) {
        walletStore.updateSession(providerSession)
        emit('close')
      }
    }
  } catch (err: any) {
    error.value = err.message || t('wallet.connectionFailed')
    logger.error('MetaMask 連接失敗:', err)
  } finally {
    isConnecting.value = false
  }
}

// 獲取 Trust Wallet provider
function getTrustWalletProvider() {
  if (typeof window === 'undefined' || !window.ethereum) return null
  
  const providers = (window.ethereum as any).providers
  if (providers && Array.isArray(providers)) {
    return providers.find((p: any) => p.isTrust) || null
  }
  return (window.ethereum as any).isTrust ? window.ethereum : null
}

// 連接 Trust Wallet
async function connectTrustWallet() {
  try {
    isConnecting.value = true
    error.value = null

    // 優先使用瀏覽器擴展（如果已安裝）
    const trustProvider = getTrustWalletProvider()
    if (trustProvider) {
      try {
        // 直接使用 Trust Wallet 擴展連接（不需要 WalletConnect）
        const accounts = await trustProvider.request({ 
          method: 'eth_requestAccounts' 
        })
        
        if (accounts && accounts.length > 0) {
          const chainIdHex = await trustProvider.request({ 
            method: 'eth_chainId' 
          })
          const chainId = parseInt(chainIdHex, 16)
          
          walletStore.setDirectConnection({
            address: accounts[0],
            chainId
          })
          
          // 檢查並切換到 BSC
          try {
            await walletStore.ensureBSCConnection()
          } catch (err) {
            logger.warn('切換到 BSC 失敗，但連接已成功')
          }
          
          emit('close')
          return
        }
      } catch (err: any) {
        if (err.code === 4001) {
          throw new Error('用戶拒絕連接請求')
        }
        throw err
      }
    }

    // 如果沒有安裝 Trust Wallet 擴展，使用 WalletConnect（手機錢包）
    if (!walletStore.connector) {
      await walletStore.initConnector()
    }

    if (!walletStore.connector) {
      throw new Error('連接器初始化失敗')
    }

    const { session: providerSession } = await walletStore.connector.connect()
    
    if (providerSession) {
      walletStore.updateSession(providerSession)
      emit('close')
    }
  } catch (err: any) {
    error.value = err.message || t('wallet.connectionFailed')
    logger.error('Trust Wallet 連接失敗:', err)
  } finally {
    isConnecting.value = false
  }
}

// 獲取 Coinbase Wallet provider
function getCoinbaseProvider() {
  if (typeof window === 'undefined' || !window.ethereum) return null
  
  const providers = (window.ethereum as any).providers
  if (providers && Array.isArray(providers)) {
    return providers.find((p: any) => p.isCoinbaseWallet) || null
  }
  return (window.ethereum as any).isCoinbaseWallet ? window.ethereum : null
}

// 連接 Coinbase Wallet
async function connectCoinbase() {
  try {
    isConnecting.value = true
    error.value = null

    // 優先使用瀏覽器擴展（如果已安裝）
    const coinbaseProvider = getCoinbaseProvider()
    if (coinbaseProvider) {
      try {
        // 直接使用 Coinbase Wallet 擴展連接（不需要 WalletConnect）
        const accounts = await coinbaseProvider.request({ 
          method: 'eth_requestAccounts' 
        })
        
        if (accounts && accounts.length > 0) {
          const chainIdHex = await coinbaseProvider.request({ 
            method: 'eth_chainId' 
          })
          const chainId = parseInt(chainIdHex, 16)
          
          walletStore.setDirectConnection({
            address: accounts[0],
            chainId
          })
          
          // 檢查並切換到 BSC
          try {
            await walletStore.ensureBSCConnection()
          } catch (err) {
            logger.warn('切換到 BSC 失敗，但連接已成功')
          }
          
          emit('close')
          return
        }
      } catch (err: any) {
        if (err.code === 4001) {
          throw new Error('用戶拒絕連接請求')
        }
        throw err
      }
    }

    // 如果沒有安裝 Coinbase Wallet 擴展，使用 WalletConnect（手機錢包）
    if (!walletStore.connector) {
      await walletStore.initConnector()
    }

    if (!walletStore.connector) {
      throw new Error('連接器初始化失敗')
    }

    const { session: providerSession } = await walletStore.connector.connect()
    
    if (providerSession) {
      walletStore.updateSession(providerSession)
      emit('close')
    }
  } catch (err: any) {
    error.value = err.message || t('wallet.connectionFailed')
    logger.error('Coinbase Wallet 連接失敗:', err)
  } finally {
    isConnecting.value = false
  }
}

// 連接 WalletConnect
async function connectWalletConnect() {
  try {
    // 開始連接流程
    isConnecting.value = true
    error.value = null

    // 切換到 mobile 標籤
    activeTab.value = 'mobile'
    
    // 確保連接器已初始化
    // 檢查連接器狀態
    if (!walletStore.connector) {
      // 初始化連接器
      try {
        await walletStore.initConnector()
        // 連接器初始化成功
      } catch (initErr: any) {
        logger.error('連接器初始化失敗:', initErr)
        throw new Error(`連接器初始化失敗: ${initErr.message || '未知錯誤'}`)
      }
    }
    
    if (!walletStore.connector) {
      throw new Error('連接器初始化失敗：connector 為 null')
    }
    
    if (!walletStore.connector.provider) {
      throw new Error('連接器初始化失敗：provider 為 null')
    }
    
    // 連接器和 provider 已就緒
    const provider = walletStore.connector.provider
    
    // 清理之前的事件監聽器
    cleanupEventListeners()
    
    // 監聽 QR 碼 URI（在連接前設置監聽器）
    const handleDisplayUri = async (uri: string) => {
      // 收到 QR 碼 URI
      qrCodeUri.value = uri
      try {
        // 使用品牌顏色生成 QR 碼
        qrCodeImage.value = await QRCode.toDataURL(uri, {
          color: {
            dark: '#3B82F6', // Neon Blue - 品牌主色
            light: '#FFFFFF' // 白色背景
          },
          margin: 2,
          width: 300,
          errorCorrectionLevel: 'M'
        })
        // QR 碼生成成功
      } catch (err) {
        logger.error('生成 QR 碼失敗:', err)
      }
    }
    provider.on('display_uri', handleDisplayUri)
    eventHandlers.push({ event: 'display_uri', handler: handleDisplayUri })
    // display_uri 事件監聽器已設置
    
    // 監聽連接完成事件（當用戶掃描 QR 碼後）
    const handleSessionUpdate = (sessionData: any) => {
      // session_update 事件觸發
      if (sessionData && sessionData.namespaces?.eip155?.accounts?.length > 0) {
        // 連接成功，更新會話
        // 連接成功，更新狀態
        isConnecting.value = false
        walletStore.resetConnectingState()
        // 清理事件監聽器
        cleanupEventListeners()
        // 延遲一點關閉，讓用戶看到連接成功的反饋
        setTimeout(() => {
          emit('close')
        }, 500)
      } else {
        logger.warn('session_update 但沒有有效的賬戶')
      }
    }
    provider.on('session_update', handleSessionUpdate)
    eventHandlers.push({ event: 'session_update', handler: handleSessionUpdate })
    // session_update 事件監聽器已設置
    
    // 監聽連接事件（備用）
    const handleConnect = () => {
      // connect 事件觸發
      isConnecting.value = false
      walletStore.resetConnectingState()
      // 清理事件監聽器
      cleanupEventListeners()
      setTimeout(() => {
        emit('close')
      }, 500)
    }
    provider.on('connect', handleConnect)
    eventHandlers.push({ event: 'connect', handler: handleConnect })
    // connect 事件監聽器已設置
    
    // 監聽錯誤事件
    const handleError = (error: any) => {
      logger.error('錯誤事件:', error)
      error.value = error?.message || 'WalletConnect 連接錯誤'
      isConnecting.value = false
      walletStore.resetConnectingState()
    }
    if (typeof provider.on === 'function') {
      try {
        provider.on('error', handleError)
        eventHandlers.push({ event: 'error', handler: handleError })
        // error 事件監聽器已設置
      } catch (err) {
        logger.warn('無法設置 error 事件監聽器:', err)
      }
    }
    
    // 監聽會話事件（用於調試）
    const handleSessionEvent = (event: any) => {
      // session_event 觸發
    }
    try {
      provider.on('session_event', handleSessionEvent)
      eventHandlers.push({ event: 'session_event', handler: handleSessionEvent })
    } catch (err) {
      logger.warn('無法設置 session_event 事件監聽器:', err)
    }
    
    // 開始連接（這會顯示 QR 碼）
    // 調用 walletStore.connect()
    try {
      const result = await walletStore.connect()
      // connect() 調用成功
      
      // 檢查是否立即有會話（可能是恢復的會話）
      if (result && walletStore.isConnected) {
        // 立即連接成功（恢復會話）
        isConnecting.value = false
        walletStore.resetConnectingState()
        cleanupEventListeners()
        setTimeout(() => {
          emit('close')
        }, 500)
        return
      }
      
      // 如果沒有立即會話，等待用戶掃描 QR 碼
      // session_update 事件會處理連接完成
      // 等待用戶掃描 QR 碼
    } catch (connectErr: any) {
      logger.error('connect() 調用失敗:', connectErr)
      // 如果是用戶取消，不顯示錯誤，只重置狀態
      if (connectErr?.message?.includes('取消') || 
          connectErr?.message?.includes('rejected') ||
          connectErr?.message?.includes('User rejected') ||
          connectErr?.code === 4001) {
        // 用戶取消了連接
        isConnecting.value = false
        walletStore.resetConnectingState()
        cleanupEventListeners()
        return
      }
      // 其他錯誤繼續拋出，由外層 catch 處理
      throw connectErr
    }
    
  } catch (err: any) {
    logger.error('連接流程失敗:', err)
    const errorMsg = err.message || t('wallet.connectionFailed')
    error.value = errorMsg
    isConnecting.value = false
    walletStore.resetConnectingState()
    // 清理事件監聽器
    cleanupEventListeners()
    // 連接失敗時不立即關閉，讓用戶看到錯誤信息
  }
}

// 複製連結
async function copyLink() {
  if (!qrCodeUri.value) return

  try {
    await navigator.clipboard.writeText(qrCodeUri.value)
    // 可以顯示一個成功提示
    // 連結已複製
  } catch (err) {
    logger.error('複製失敗:', err)
  }
}

// 關閉模態框
function close() {
  emit('close')
}

// 事件監聽器引用（用於清理）
let eventHandlers: Array<{ event: string; handler: Function }> = []

// 清理事件監聽器
function cleanupEventListeners() {
  if (walletStore.connector?.provider) {
    const provider = walletStore.connector.provider
    // 移除所有事件監聽器
    eventHandlers.forEach(({ event, handler }) => {
      try {
        provider.off(event, handler as any)
      } catch (err) {
        logger.warn('移除事件監聽器失敗:', err)
      }
    })
    eventHandlers = []
  }
}

// 監聽模態框打開/關閉
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    // 模態框打開時
    detectWallets()
    activeTab.value = 'desktop'
    error.value = null
    qrCodeUri.value = null
    qrCodeImage.value = ''
    isConnecting.value = false
    // 重置 store 的連接狀態
    walletStore.resetConnectingState()
  } else {
    // 模態框關閉時，清理所有狀態
    isConnecting.value = false
    error.value = null
    qrCodeUri.value = null
    qrCodeImage.value = ''
    // 清理事件監聽器
    cleanupEventListeners()
    // 重置 store 的連接狀態
    walletStore.resetConnectingState()
  }
})

onMounted(() => {
  detectWallets()
})
</script>

<style scoped>
.wallet-selector-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.wallet-selector-modal {
  background: var(--bg-primary, #1a1a1a);
  border-radius: 1.5rem;
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.wallet-selector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.wallet-selector-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary, #ffffff);
}

.close-button {
  background: transparent;
  border: none;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary, #ffffff);
}

.wallet-selector-tabs {
  display: flex;
  padding: 0 1.5rem;
  gap: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  background: transparent;
  border: none;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.tab-button.active {
  color: var(--color-primary, #667eea);
  border-bottom-color: var(--color-primary, #667eea);
}

.tab-button:hover {
  color: var(--text-primary, #ffffff);
}

.wallet-options {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.wallet-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
}

.wallet-option:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.wallet-option:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wallet-option.unavailable {
  opacity: 0.6;
}

.wallet-icon {
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.wallet-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.trust-wallet-icon,
.coinbase-wallet-icon {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  color: #ffffff;
}

.trust-wallet-icon {
  background: linear-gradient(135deg, #3375bb 0%, #1e3a5f 100%);
}

.coinbase-wallet-icon {
  background: linear-gradient(135deg, #0052ff 0%, #0038a3 100%);
}

.walletconnect-icon {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b99fc;
}

.wallet-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.wallet-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary, #ffffff);
}

.wallet-status {
  font-size: 0.75rem;
}

.status-available {
  color: #10b981;
}

.status-unavailable {
  color: #ef4444;
}

.wallet-arrow {
  color: var(--text-secondary, #9ca3af);
  flex-shrink: 0;
}

.wallet-qr-section {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.qr-code-container {
  width: 100%;
  max-width: 300px;
  aspect-ratio: 1;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%);
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 30px rgba(59, 130, 246, 0.2),
              inset 0 0 20px rgba(59, 130, 246, 0.05);
  position: relative;
  overflow: hidden;
}

.qr-code-container::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
  animation: pulse-glow 3s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

.qr-code {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
  background: #ffffff;
  border-radius: 0.5rem;
  padding: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.qr-code img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 0 10px rgba(59, 130, 246, 0.3));
}

.qr-code-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--text-secondary, #9ca3af);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: var(--color-primary, #667eea);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.qr-instruction {
  color: var(--text-secondary, #9ca3af);
  font-size: 0.875rem;
  text-align: center;
  margin: 0;
}

.copy-link-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  color: var(--text-primary, #ffffff);
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.copy-link-button:hover {
  background: rgba(255, 255, 255, 0.15);
}

.wallet-error {
  padding: 1rem 1.5rem;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  margin: 0 1.5rem 1.5rem;
}

/* Modal 動畫 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .wallet-selector-modal,
.modal-leave-active .wallet-selector-modal {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from .wallet-selector-modal,
.modal-leave-to .wallet-selector-modal {
  transform: scale(0.9);
  opacity: 0;
}
</style>

